import { useState } from 'react'
import { Skeleton, Stack } from '@chakra-ui/react'
import type { AxiosError } from 'axios'

import { PolicyVersionStatus } from '@/dtos/exceptionalDiscount'
import { useDiscountEligibility } from '@/hooks/useDiscountEligibility'
import { useExceptionalDiscount } from '@/hooks/useExceptionalDiscount'
import { usePolicyVersions } from '@/hooks/usePolicyVersions'
import { IndexaceSection } from './IndexaceSection'
import { DeletePendingDialog } from './sleva/DeletePendingDialog'
import { SlevaDialog } from './sleva/SlevaDialog'
import { VerzeTimeline } from './verze/VerzeTimeline'

const errorTextOf = (error: unknown): string | undefined => {
  if (!error) return undefined
  const axiosError = error as AxiosError<{ errors?: string[] }>

  return axiosError.response?.data?.errors?.join(' ')
    ?? 'Operace se nezdařila. Zkuste to prosím znovu.'
}

export const PolicyVersionsContainer = ({ insuranceId }: { insuranceId: number }) => {
  const { data: versionData, isLoading: isLoadingVersions } = usePolicyVersions(insuranceId)
  const { data: eligibility } = useDiscountEligibility(insuranceId)
  const { apply, deletePending } = useExceptionalDiscount(insuranceId)

  const [slevaOpen, setSlevaOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const versions = versionData?.versions ?? []
  const activeVersion = versions.find(v => v.status === PolicyVersionStatus.active)
  const pendingVersion = versions.find(v => v.status === PolicyVersionStatus.pending)

  const openSleva = () => {
    apply.reset()
    setSlevaOpen(true)
  }

  const openDelete = () => {
    deletePending.reset()
    setDeleteOpen(true)
  }

  if (isLoadingVersions) {
    return (
      <Stack gap={3}>
        <Skeleton height={8} />
        <Skeleton height={32} />
      </Stack>
    )
  }

  return (
    <>
      <IndexaceSection
        count={versions.length}
        canApplyDiscount={!!eligibility?.canApplyDiscount && !!activeVersion}
        disabledReasons={eligibility?.reasons ?? []}
        onApplyClick={openSleva}
      >
        <VerzeTimeline
          versions={versions}
          deletablePendingId={eligibility?.hasDeletablePending ? pendingVersion?.policyVersionId : undefined}
          onDeletePending={openDelete}
        />
      </IndexaceSection>

      {slevaOpen && activeVersion && (
        <SlevaDialog
          isOpen={slevaOpen}
          onClose={() => setSlevaOpen(false)}
          activeVersion={activeVersion}
          insuranceId={insuranceId}
          isSubmitting={apply.isPending}
          submitError={errorTextOf(apply.error)}
          onSubmit={request => {
            apply.mutate(request, { onSuccess: () => setSlevaOpen(false) })
          }}
        />
      )}

      <DeletePendingDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        isDeleting={deletePending.isPending}
        deleteError={errorTextOf(deletePending.error)}
        onConfirm={() => {
          deletePending.mutate({ insuranceId }, { onSuccess: () => setDeleteOpen(false) })
        }}
      />
    </>
  )
}
